import { getAuthToken } from '../firebase/firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Create a shareable link for an itinerary
 * @param {Object} shareData - The share data (itineraryId, itineraryData, expiresIn)
 * @returns {Promise<Object>} The generated share data including URL
 */
export const createShareLink = async (shareData) => {
  try {
    // Try to get auth token if user is logged in
    let token = null;
    try {
      token = await getAuthToken();
    } catch (error) {
      // Continue without token for sharing functionality
      console.log('User not authenticated, creating anonymous share');
    }
    
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API_BASE_URL}/shares/create`, {
      method: 'POST',
      headers,
      body: JSON.stringify(shareData)
    });
    
    // Check for HTML response (common error where server returns HTML instead of JSON)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      console.error('Received HTML response instead of JSON');
      throw new Error('Invalid response from server (HTML received)');
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to create share link' }));
      throw new Error(errorData.message || 'Failed to create share link');
    }
    
    const data = await response.json();
    
    // Format the share URL based on the returned shareId
    // Use a regular URL format without the @ symbol
    const baseUrl = window.location.origin || 'https://roamzap.com';
    // Remove any potential @ symbol that might be in the URL
    const cleanBaseUrl = baseUrl.replace(/^@/, '').replace(/@/g, '');
    const shareUrl = `${cleanBaseUrl}/itinerary?shareId=${data.data.shareId}`;
    
    console.log("Generated share URL:", shareUrl); // Debug log
    
    return { ...data.data, shareUrl };
  } catch (error) {
    console.error('Error creating share link:', error);
    throw new Error('Failed to create share link');
  }
};

/**
 * Get a shared itinerary using a share ID
 * @param {string} shareId - The share ID
 * @returns {Promise<Object>} The shared itinerary data
 */
export const getSharedItinerary = async (shareId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/shares/${shareId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    // Check for HTML response (common error where server returns HTML instead of JSON)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      console.error('Received HTML response instead of JSON');
      throw new Error('Invalid response from server (HTML received)');
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch shared itinerary' }));
      throw new Error(errorData.message || 'Failed to fetch shared itinerary');
    }
    
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error(`Error fetching shared itinerary with ID ${shareId}:`, error);
    throw new Error('Failed to fetch shared itinerary');
  }
};

/**
 * Delete a share link
 * @param {string} shareId - The share ID to delete
 * @returns {Promise<Object>} Result of the deletion
 */
export const deleteShareLink = async (shareId) => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/shares/${shareId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    // Check for HTML response (common error where server returns HTML instead of JSON)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      console.error('Received HTML response instead of JSON');
      throw new Error('Invalid response from server (HTML received)');
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to delete share link' }));
      throw new Error(errorData.message || 'Failed to delete share link');
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error deleting share link ${shareId}:`, error);
    throw new Error('Failed to delete share link');
  }
};

/**
 * Get all share links created by the current user
 * @returns {Promise<Array>} List of share links
 */
export const getUserShares = async () => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/shares`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    // Check for HTML response (common error where server returns HTML instead of JSON)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      console.error('Received HTML response instead of JSON');
      throw new Error('Invalid response from server (HTML received)');
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch share links' }));
      throw new Error(errorData.message || 'Failed to fetch share links');
    }
    
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching share links:', error);
    throw new Error('Failed to fetch share links');
  }
}; 